// ============================================================
// AdminDashboardWithCodeSnippets.jsx
//
// A high-fidelity, branded Admin Dashboard for user management.
//
// Author: ProDev Architecture Series
// Features: Dynamic role updates (Promote/Demote), axios-based
//           CRUD operations, and inline documentation snippets.
// ============================================================

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PeopleAltIcon          from "@mui/icons-material/PeopleAlt";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import PersonAddIcon          from "@mui/icons-material/PersonAdd";
import SecurityIcon           from "@mui/icons-material/Security";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 12, fontWeight: 700, fontFamily: "monospace" }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Dashboard Component ──────────────────────────────

export default function AdminDashboardWithCodeSnippets() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showScroll, setShowScroll] = useState(false);

    const fetchUsers = async () => {
        try {
            const res = await axios.get("/api/admin/users", {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            setUsers(res.data);
        } catch (err) {
            console.error("Error fetching users", err);
        } finally {
            setLoading(false);
        }
    };

    const updateRole = async (id, newRole) => {
        try {
            await axios.patch(
                `/api/admin/users/${id}/role`,
                { role: newRole },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );
            fetchUsers();
        } catch (err) {
            alert("Failed to update role");
        }
    };

    useEffect(() => {
        fetchUsers();
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SecurityIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Internal Management · v2.0"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Admin <span>Dashboard</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Oversee system integrity by managing user access levels. This portal allows for real-time promotion and demotion of users while serving as a live documentation tool for the underlying code logic.
                    </Typography>
                </Box>

                {/* 1. OVERVIEW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LightbulbIcon />} label="Permissions Architecture" />
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Typography sx={{ color: BRAND.muted, mb: 2 }}>
                            Access control follows a strictly hierarchical model. All registered entities default to the <strong style={{color: BRAND.white}}>User</strong> tier. Administrative elevation grants destructive permissions and access to private telemetry data.
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Chip label="User: Read-Only" sx={{ color: BRAND.muted, borderColor: BRAND.border }} variant="outlined" />
                            <Chip label="Admin: Read/Write/Delete" sx={{ color: BRAND.gold, borderColor: BRAND.gold }} variant="outlined" />
                        </Stack>
                    </Paper>
                    <CodeBlock
                        title="JSX Overview Structure"
                        code={`<Box sx={styles.sectionHeader}>\n  <Avatar><LightbulbIcon /></Avatar>\n  <Typography variant="h6">Overview</Typography>\n</Box>`}
                    />
                </Box>

                {/* 2. USER TABLE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PeopleAltIcon />} label="User Access Control" />
                    <TableContainer component={Paper} sx={{ background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, mb: 2 }}>
                        <Table>
                            <TableHead sx={{ background: BRAND.goldGlow }}>
                                <TableRow>
                                    <TableCell sx={{ color: BRAND.gold, fontWeight: 800 }}>Account Identifier</TableCell>
                                    <TableCell sx={{ color: BRAND.gold, fontWeight: 800 }}>Assigned Role</TableCell>
                                    <TableCell align="right" sx={{ color: BRAND.gold, fontWeight: 800 }}>Privilege Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={24} sx={{ color: BRAND.gold }} /></TableCell></TableRow>
                                ) : (
                                    users.map((user) => (
                                        <TableRow key={user._id} sx={{ '&:hover': { background: 'rgba(255,255,255,0.02)' } }}>
                                            <TableCell sx={{ color: BRAND.white, fontFamily: "monospace" }}>{user.email}</TableCell>
                                            <TableCell>
                                                <Chip label={user.role} size="small" sx={{ background: user.role === 'admin' ? BRAND.goldGlow : 'transparent', color: user.role === 'admin' ? BRAND.gold : BRAND.muted, border: `1px solid ${BRAND.border}` }} />
                                            </TableCell>
                                            <TableCell align="right">
                                                {user.role !== "admin" ? (
                                                    <Button variant="outlined" color="success" size="small" onClick={() => updateRole(user._id, "admin")} startIcon={<CheckCircleOutlineIcon />}>Promote</Button>
                                                ) : (
                                                    <Button variant="outlined" color="error" size="small" onClick={() => updateRole(user._id, "user")}>Demote</Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <CodeBlock
                        title="Mapping Table Logic"
                        code={`{users.map((u) => (\n  <TableRow key={u._id}>\n    <TableCell>{u.email}</TableCell>\n    <TableCell>{u.role}</TableCell>\n    <TableCell>\n      {u.role === 'admin' ? <DemoteBtn /> : <PromoteBtn />}\n    </TableCell>\n  </TableRow>\n))}`}
                    />
                </Box>

                {/* 3. SYSTEM LOGIC SUMMARY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AdminPanelSettingsIcon />} label="System Access Policies" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Standard User", desc: "Access to personalized dashboard and basic interaction endpoints.", icon: <StarIcon /> },
                            { title: "Administrator", desc: "Full CRUD access, environment configuration, and user escalation rights.", icon: <SecurityIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={6} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. API CREATION DOCS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PersonAddIcon />} label="New Identity Provisioning" />
                    <Alert severity="info" sx={{ background: BRAND.goldGlow, color: BRAND.gold, mb: 3, border: `1px solid ${BRAND.border}`, "& .MuiAlert-icon": { color: BRAND.gold } }}>
                        User creation should always validate email uniqueness before hashing passwords in the backend.
                    </Alert>
                    <CodeBlock
                        title="User Creation Hook (Axios)"
                        code={`axios.post("/api/admin/users", { email, password, role }, {\n  headers: { Authorization: \`Bearer \${token}\` }\n})\n.then(() => alert("Identity Created"))\n.catch(err => console.error(err));`}
                    />
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
